import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Section, EmptyState, StatusBadge } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const ROLES = ["owner", "manager", "cashier", "waiter", "kitchen", "accountant"];

export default function Users() {
  const { t, role } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", password: "", role: "waiter", phone: "", status: "active" });
  const roleOptions = me?.role === "owner" ? ROLES : ROLES.filter((r) => r !== "owner" && r !== "manager");

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (edit) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.username; // username cannot be updated
        await api.put(`/users/${edit.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success(t("success"));
      setOpen(false); setEdit(null);
      setForm({ name: "", username: "", password: "", role: "waiter", phone: "", status: "active" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("users")}</h1>
        <button
          data-testid="add-user"
          onClick={() => { setEdit(null); setForm({ name: "", username: "", password: "", role: "waiter", phone: "", status: "active" }); setOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]"
        >
          <Plus className="w-4 h-4" /> {t("addUser")}
        </button>
      </div>

      <Section title={t("users")}>
        {users.length === 0 ? <EmptyState title={t("empty")} /> : (
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-right text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr>
                  <th className="p-3">{t("name")}</th>
                  <th className="p-3">{t("username")}</th>
                  <th className="p-3">{t("role")}</th>
                  <th className="p-3">{t("phone")}</th>
                  <th className="p-3">{t("status")}</th>
                  <th className="p-3">{t("lastLogin")}</th>
                  <th className="p-3">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100">
                    <td className="p-3 text-gray-900 font-medium">{u.name}</td>
                    <td className="p-3 font-mono text-gray-700">{u.username}</td>
                    <td className="p-3">{role(u.role)}</td>
                    <td className="p-3 text-gray-500 tabular">{u.phone || "-"}</td>
                    <td className="p-3"><StatusBadge status={u.status === "active" ? "active" : "cancelled"} label={u.status === "active" ? t("active") : t("cancelled")} /></td>
                    <td className="p-3 text-gray-500 text-xs tabular">{u.last_login ? u.last_login.slice(0, 16).replace("T", " ") : "-"}</td>
                    <td className="p-3 flex gap-2">
                      <button onClick={() => { setEdit(u); setForm({ name: u.name, username: u.username, password: "", role: u.role, phone: u.phone || "", status: u.status }); setOpen(true); }} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><Pencil className="w-3.5 h-3.5" /></button>
                      {me?.role === "owner" && u.id !== me?.id && (
                        <button onClick={() => del(u.id)} className="p-2 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader><DialogTitle>{edit ? t("edit") : t("addUser")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("name")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input value={form.username} disabled={!!edit} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder={t("username")} className="w-full h-11 px-3 rounded-xl border border-gray-200 disabled:bg-gray-50 font-mono" />
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={edit ? "•••••• (تغییر)" : t("password")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <RpSelect
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
              options={roleOptions.map((r) => ({ value: r, label: role(r) }))}
            />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("phone")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <RpSelect
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v })}
              options={[
                { value: "active", label: t("active") },
                { value: "inactive", label: t("cancelled") },
              ]}
            />
          </div>
          <DialogFooter className="flex-row-reverse">
            <button data-testid="save-user" onClick={save} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("save")}</button>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaggerRoot>
  );
}
